// Login.tsx
import { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Link as MuiLink,
  Paper,
  Divider
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../context/AuthContext';

const brandImgs = [
  { src: '/reddit.png', size: 120, top: '6%', left: '8%', rot: -8 },
  { src: '/google.png', size: 130, top: '10%', right: '8%', rot: 10 },
  { src: '/youtube.png', size: 140, bottom: '10%', left: '10%', rot: -6 },
  { src: '/stacko.png', size: 120, bottom: '14%', right: '12%', rot: 6 },
  { src: '/x.webp', size: 110, top: '40%', left: '45%', rot: 0 },
  { src: '/gyik.png', size: 120, top: '68%', left: '30%', rot: -4 },
];

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError('Hibás email cím vagy jelszó');
      console.error('Login error:', err);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', position: 'relative', overflow: 'hidden' }}>
      {/* 1) Alap: erős, több-rétegű gradient – mód-függő */}
      <Box
        aria-hidden
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 0,
          backgroundColor: (t) => (t.palette.mode === 'dark' ? '#0b0f17' : '#f6f8fd'),
          backgroundImage: (t) =>
            t.palette.mode === 'dark'
              ? [
                  'radial-gradient(1000px 600px at 8% -10%, rgba(99,102,241,.25), transparent 60%)',
                  'radial-gradient(900px 540px at 110% 10%, rgba(16,185,129,.22), transparent 55%)',
                  'linear-gradient(180deg, rgba(255,255,255,.03), rgba(255,255,255,.02))',
                ].join(', ')
              : [
                  'radial-gradient(1000px 600px at 8% -10%, rgba(99,102,241,.18), transparent 60%)',
                  'radial-gradient(900px 540px at 110% 10%, rgba(16,185,129,.16), transparent 55%)',
                  'linear-gradient(180deg, rgba(0,0,0,.03), rgba(0,0,0,.02))',
                ].join(', '),
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
        }}
      />

      {/* 2) Ikon-kollázs: blur + blend, mód-függő fényerő/opacity */}
      {brandImgs.map((b, i) => (
        <Box
          aria-hidden
          key={i}
          component="img"
          src={b.src}
          alt=""
          sx={{
            position: 'fixed',
            zIndex: 0,
            ...(b.top ? { top: b.top } : {}),
            ...(b.bottom ? { bottom: b.bottom } : {}),
            ...(b.left ? { left: b.left } : {}),
            ...(b.right ? { right: b.right } : {}),
            width: b.size,
            height: b.size,
            opacity: (t) => (t.palette.mode === 'dark' ? 0.12 : 0.10),
            filter: (t) =>
              t.palette.mode === 'dark'
                ? 'grayscale(1) blur(1.2px) brightness(1.35)'
                : 'grayscale(1) blur(1px) brightness(0.85)',
            transform: `rotate(${b.rot}deg)`,
            mixBlendMode: (t) => (t.palette.mode === 'dark' ? 'screen' : 'multiply'),
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        />
      ))}

      {/* 3) Grid-overlay: dark módban világos vonalak, light módban sötétebbek */}
      <Box
        aria-hidden
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 0,
          backgroundImage: (t) =>
            t.palette.mode === 'dark'
              ? 'linear-gradient(rgba(255,255,255,.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.06) 1px, transparent 1px)'
              : 'linear-gradient(rgba(0,0,0,.06) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,.06) 1px, transparent 1px)',
          backgroundSize: '28px 28px, 28px 28px',
          maskImage:
            'radial-gradient(1200px 800px at 50% 40%, rgba(0,0,0,1) 60%, rgba(0,0,0,0) 100%)',
          pointerEvents: 'none',
        }}
      />

      {/* fő layout */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.1fr 0.9fr' },
          alignItems: 'center',
          gap: { xs: 2, md: 6 },
          maxWidth: 1100,
          mx: 'auto',
          px: { xs: 2, sm: 4, md: 6 },
          py: { xs: 6, md: 10 },
          minHeight: '100vh',
        }}
      >
        {/* bal oldali hero */}
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              letterSpacing: -0.5,
              mb: 1,
              textShadow: (t) =>
                t.palette.mode === 'dark'
                  ? '0 1px 0 rgba(0,0,0,.6)'
                  : '0 1px 0 rgba(255,255,255,.8)',
            }}
          >
            Reddit Analyzer
          </Typography>
          <Typography
            variant="h6"
            sx={{
              opacity: 0.85,
              maxWidth: 640,
              lineHeight: 1.5,
            }}
          >
            Többplatformos hangulatelemző projekt – Reddit, Google, YouTube, Stack Overflow, X,
            GYIK forrásokhoz igazított munkafolyamatokkal. Cél: hozzászólások, vélemények,
            visszajelzések <strong>gyors és áttekinthető</strong> elemzése.
          </Typography>

          <Divider sx={{ my: 3, opacity: 0.4 }} />

          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            <strong>Megjegyzés:</strong> ez egy <em>elemző</em> demó/projekt felület. Bejelentkezés
            után URL-eken futtathatsz feldolgozást, és megnézheted a kommentek
            pontszámait/statisztikáit.
          </Typography>
        </Box>

        {/* jobb oldali kártya */}
        <Paper
          elevation={0}
          sx={{
            backdropFilter: 'blur(10px)',
            backgroundColor: (t) =>
              t.palette.mode === 'dark'
                ? 'rgba(18, 22, 30, 0.75)'
                : 'rgba(255,255,255,0.72)',
            border: '1px solid',
            borderColor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,.08)' : 'rgba(0,0,0,.08)'),
            p: { xs: 3, sm: 4 },
            borderRadius: 3,
            boxShadow: (t) =>
              t.palette.mode === 'dark'
                ? '0 10px 30px rgba(0,0,0,.45)'
                : '0 10px 30px rgba(0,0,0,.08)',
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
            Bejelentkezés
          </Typography>

          <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>
            Lépj be és indíts elemzéseket! <em>URL</em> linkekhez optimalizálva.
          </Typography>

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              label="Email"
              fullWidth
              margin="normal"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError('');
              }}
              error={!!error}
            />

            <TextField
              label="Jelszó"
              type="password"
              fullWidth
              margin="normal"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError('');
              }}
              error={!!error}
            />

            {error && (
              <Typography
                color="error"
                sx={{
                  mt: 1,
                  mb: 2,
                  p: 1.5,
                  borderRadius: 1,
                  backgroundColor: (t) =>
                    t.palette.mode === 'dark'
                      ? 'rgba(211,47,47,.18)'
                      : 'rgba(244,67,54,.15)',
                  border: '1px solid',
                  borderColor: 'error.main',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                }}
              >
                {error}
              </Typography>
            )}

            <Button type="submit" variant="contained" sx={{ mt: 1 }} fullWidth>
              Belépés
            </Button>
          </Box>

          <Typography variant="body2" sx={{ mt: 2 }}>
            Nincs még fiókod?{' '}
            <MuiLink component={RouterLink} to="/register">
              Regisztrálj itt
            </MuiLink>
          </Typography>

          <Divider sx={{ my: 3, opacity: 0.4 }} />
          {/* apró platform-csík (a kollázs képeiből) */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(6, minmax(0,1fr))',
              gap: 1.5,
              alignItems: 'center',
              opacity: 0.9,
            }}
          >
            {['/reddit.png','/google.png','/youtube.png','/stacko.png','/x.webp','/gyik.png'].map((src, i) => (
              <Box key={i} component="img" src={src} alt="brand" sx={{ width: 26, height: 26, mx: 'auto', filter: 'grayscale(1)' }} />
            ))}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
